import React, { useState, useEffect, useCallback } from 'react';

interface DipSwitchHelperProps {
  switchCount: number;
  initialValue: number;
  onConfigChange?: (config: { switchCount: number; decimalValue: number }) => void;
  editable?: boolean;
  showHexBinary?: boolean;
}

const DipSwitchHelper: React.FC<DipSwitchHelperProps> = ({
  switchCount: initialSwitchCount,
  initialValue,
  onConfigChange,
  editable = true,
  showHexBinary = true
}) => {
  const [switchCount, setSwitchCount] = useState<number>(initialSwitchCount);
  const [switches, setSwitches] = useState<boolean[]>(() => {
    const initialSwitches = Array(16).fill(false);
    for (let i = 0; i < initialSwitchCount; i++) {
      initialSwitches[i] = !!(initialValue & (1 << i));
    }
    return initialSwitches;
  });
  const [decimalValue, setDecimalValue] = useState<number>(initialValue);
  const [showLabels, setShowLabels] = useState<boolean>(true); // Changed to true
  const [direction, setDirection] = useState<'ltr' | 'rtl'>('ltr');


  const calculateDecimalValue = useCallback(() => {
    let newDecimalValue = 0;
    for (let i = 0; i < switchCount; i++) {
      const powerIndex = direction === 'ltr' ? i : switchCount - 1 - i;
      if (switches[i]) {
        newDecimalValue += Math.pow(2, powerIndex);
      }
    }
    return newDecimalValue;
  }, [switches, switchCount, direction]);

  // Only trigger decimal value update when switches or switchCount change
  useEffect(() => {
    const newDecimalValue = calculateDecimalValue();
    if (newDecimalValue !== decimalValue) {
      setDecimalValue(newDecimalValue);
      if (onConfigChange) {
        onConfigChange({ switchCount, decimalValue: newDecimalValue });
      }
    }
  }, [switches, switchCount, direction, decimalValue, onConfigChange, calculateDecimalValue]);


  const toggleSwitch = useCallback((index: number) => {
    setSwitches(prev => {
      const newSwitches = [...prev];
      newSwitches[index] = !newSwitches[index];
      return newSwitches;
    });
  }, []);

  const handleDecimalInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
    if (isNaN(value)) return;
    setDecimalValue(value);
    const newSwitches = Array(switchCount).fill(false);
    for (let i = 0; i < switchCount; i++) {
      const powerIndex = direction === 'ltr' ? i : switchCount - 1 - i;
      newSwitches[i] = !!(value & (1 << powerIndex));
    }
    setSwitches(newSwitches);
  }, [switchCount, direction]);

  return (
    <div className="w-full">      
      <div className="mb-4 bg-gray-200 p-2 rounded-md inline-block">
        <div className="flex flex-wrap justify-center">
          {Array(switchCount).fill(null).map((_, index) => {
            const isOn = switches[index];
            return (
              <div key={index} className="m-1">
                {showLabels && (
                  <div className={`text-xs font-bold mb-1 ${isOn ? 'text-gray-800' : 'text-gray-500'}`}>
                    {isOn ? 'ON' : 'OFF'}
                  </div>
                )}
                <button
                  onClick={() => toggleSwitch(index)}
                  className="w-6 h-10 sm:w-8 sm:h-12 bg-gray-100 border border-gray-300 relative overflow-hidden"
                  disabled={!editable}
                >
                  <div className="absolute inset-0 flex flex-col">
                    <div className={`w-full h-1/2 transition-colors duration-300 ease-in-out ${isOn ? 'bg-gray-800' : 'bg-gray-300'}`}></div>
                    <div className={`w-full h-1/2 transition-colors duration-300 ease-in-out ${isOn ? 'bg-gray-300' : 'bg-gray-800'}`}></div>
                  </div>
                </button>
                <div className="text-xs text-center text-gray-800 mt-1">{index + 1}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Decimal Value:</label>
          <input
            type="number"
            value={decimalValue}
            onChange={handleDecimalInput}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            disabled={!editable}
          />
        </div>
        {showHexBinary && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Hexadecimal Value:</label>
              <input
                type="text"
                value={decimalValue.toString(16).toUpperCase().padStart(2, '0')}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Binary Value:</label>
              <input
                type="text"
                value={decimalValue.toString(2).padStart(switchCount, '0')}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
              />
            </div>
          </>
        )}
      </div>
      

      {editable && (
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Number of switches:</label>
            <select
              value={switchCount}
              onChange={(e) => {
                setSwitchCount(parseInt(e.target.value, 10));
                setSwitches(Array(16).fill(false));
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            >
              <option value={4}>4</option>
              <option value={8}>8</option>
              <option value={16}>16</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Show ON/OFF labels:</label>
            <input
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
              className="rounded text-indigo-600 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Switch direction:</label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as 'ltr' | 'rtl')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            >
              <option value="ltr">Left to Right (1 to {Math.pow(2, switchCount-1)})</option>
              <option value="rtl">Right to Left ({Math.pow(2, switchCount-1)} to 1)</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default DipSwitchHelper;
